import { useState, useEffect, type FormEvent } from 'react';
import { Modal, Input, Select, Button, Toggle } from '@nexora/design-system';
import { isInForce, type TaxRate, type TaxRateInput, type TaxType } from './useTaxRates';

const IMPOSTO_OPTIONS = [
  { value: 'ICMS', label: 'ICMS' },
  { value: 'ISS', label: 'ISS' },
  { value: 'PIS', label: 'PIS' },
  { value: 'COFINS', label: 'COFINS' },
  { value: 'IPI', label: 'IPI' },
  { value: 'IBS', label: 'IBS' },
  { value: 'CBS', label: 'CBS' },
];

interface TaxRateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  taxRate?: TaxRate;
  onSubmit: (data: TaxRateInput) => Promise<void>;
}

export function TaxRateFormModal({ isOpen, onClose, taxRate, onSubmit }: TaxRateFormModalProps): JSX.Element {
  const [imposto, setImposto] = useState<string>('ICMS');
  const [aliquota, setAliquota] = useState<string>('');
  const [ncmPrefix, setNcmPrefix] = useState<string>('');
  const [uf, setUf] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [ativo, setAtivo] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (taxRate) {
      setImposto(taxRate.taxType);
      setAliquota(String(taxRate.aliquota));
      setNcmPrefix(taxRate.ncmPrefix ?? '');
      setUf(taxRate.ufOrigem ?? '');
      setDataInicio(taxRate.effectiveSince.split('T')[0] ?? taxRate.effectiveSince);
      setDataFim(taxRate.effectiveUntil?.split('T')[0] ?? '');
      setAtivo(isInForce(taxRate));
    } else {
      setImposto('ICMS');
      setAliquota('');
      setNcmPrefix('');
      setUf('');
      setDataInicio('');
      setDataFim('');
      setAtivo(true);
    }
    setError(null);
  }, [taxRate, isOpen]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    const aliquotaNum = parseFloat(aliquota);
    if (isNaN(aliquotaNum) || aliquotaNum < 0) {
      setError('Alíquota deve ser um número válido maior ou igual a zero.');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({
        taxType: imposto as TaxType,
        aliquota: aliquotaNum,
        ncmPrefix: ncmPrefix || null,
        ufOrigem: uf || null,
        effectiveSince: dataInicio,
        effectiveUntil: dataFim || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar aliquota.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = taxRate ? 'Editar Alíquota' : 'Nova Alíquota';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select
            label="Imposto"
            value={imposto}
            onChange={(e) => setImposto(e.target.value)}
          >
            {IMPOSTO_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>

          <Input
            label="Alíquota (%)"
            type="number"
            step="0.01"
            min="0"
            value={aliquota}
            onChange={(e) => setAliquota(e.target.value)}
            required
          />

          <Input
            label="Prefixo NCM"
            value={ncmPrefix}
            onChange={(e) => setNcmPrefix(e.target.value)}
            placeholder="Ex: 8471"
          />

          <Input
            label="UF"
            value={uf}
            onChange={(e) => setUf(e.target.value.toUpperCase())}
            maxLength={2}
            placeholder="Ex: SP"
          />

          <Input
            label="Vigencia inicial"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            required
          />

          <Input
            label="Vigencia final"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />

          <Toggle
            label="Ativo"
            checked={ativo}
            onChange={setAtivo}
          />

          {error && (
            <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>
              {error}
            </p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e3e5',
          }}
        >
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
